/**
 * "Dashboard e report" (gruppo Condivisi): unica destinazione che riunisce
 * la vecchia Dashboard commerciale e il Report, più analisi operative per
 * servizio ed economiche.
 *
 * - Panoramica: KPI e follow-up della dashboard commerciale (invariati).
 * - Operativo: riepilogo per servizio + report commerciale con export CSV.
 * - Economico: aggregati da fonti verificate (tutti gli utenti admin autenticati).
 *
 * Filtri comuni: periodo, servizio (Tutti / CDRPT / Affidya) e operatore dove
 * la metrica è attribuibile. I filtri restano salvati nella sessione quando
 * si passa al dettaglio di un modulo e si torna indietro.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Download, ExternalLink, LayoutDashboard, ListChecks, RefreshCw, Share2, Wallet } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import CommercialeDashboardModule from "./CommercialeDashboardModule";
import ReportCommercialeModule from "./ReportCommercialeModule";
import {
  AdminPageHeader,
  KpiTile,
  NON_DISPONIBILE,
  StateBlock,
  StatusBadge,
  formatCount,
  formatEuro,
} from "./components/AdminUi";
import { DASHBOARD_TABS, dashboardTabAccess, defaultDashboardTab } from "./adminNavConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const FILTERS_KEY = "affidya_dashboard_report_filters_v1";

const PERIOD_PRESETS = [
  { id: "30", label: "Ultimi 30 giorni" },
  { id: "90", label: "Ultimi 90 giorni" },
  { id: "365", label: "Ultimi 12 mesi" },
  { id: "mese", label: "Mese corrente" },
  { id: "anno", label: "Anno corrente" },
  { id: "custom", label: "Intervallo personalizzato" },
];

const SERVIZI = [
  { id: "tutti", label: "Tutti" },
  { id: "cdrpt", label: "CDRPT" },
  { id: "affidya", label: "Affidya" },
];

const MISURE = [
  ["maturato", "Maturato"],
  ["fatturato", "Fatturato"],
  ["incassato", "Incassato"],
  ["residuo_da_fatturare", "Residuo da fatturare"],
  ["crediti_non_incassati", "Crediti non incassati"],
];

function isoDay(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function periodFromPreset(preset, today = new Date()) {
  const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (preset === "mese") return { dateFrom: isoDay(new Date(end.getFullYear(), end.getMonth(), 1)), dateTo: isoDay(end) };
  if (preset === "anno") return { dateFrom: isoDay(new Date(end.getFullYear(), 0, 1)), dateTo: isoDay(end) };
  const days = Number(preset) || 30;
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { dateFrom: isoDay(start), dateTo: isoDay(end) };
}

function loadFilters() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(FILTERS_KEY) || "null");
    if (saved && saved.preset) {
      const period = saved.preset === "custom" ? { dateFrom: saved.dateFrom, dateTo: saved.dateTo } : periodFromPreset(saved.preset);
      return { servizio: "tutti", operatore: "", ...saved, ...period };
    }
  } catch {
    /* filtri non leggibili: si usano i default */
  }
  return { preset: "30", servizio: "tutti", operatore: "", ...periodFromPreset("30") };
}

function authHeaders() {
  const token = localStorage.getItem("super_admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function queryString(params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") qs.set(key, value);
  });
  return qs.toString();
}

const STATO_BADGE = {
  verificato: { tone: "success", label: "Fonte verificata" },
  parziale: { tone: "warning", label: "Parziale" },
  non_disponibile: { tone: "neutral", label: NON_DISPONIBILE },
};

function StatoFonte({ stato }) {
  const cfg = STATO_BADGE[stato] || STATO_BADGE.non_disponibile;
  return <StatusBadge tone={cfg.tone}>{cfg.label}</StatusBadge>;
}

export default function DashboardReportModule({ adminUser = {}, activeTab, onTabChange, onNavigate }) {
  const access = useMemo(() => dashboardTabAccess(adminUser || {}), [adminUser]);
  const allowedTabs = DASHBOARD_TABS.filter((t) => access[t.id]);
  const tab = allowedTabs.some((t) => t.id === activeTab) ? activeTab : defaultDashboardTab(access);
  const [filters, setFilters] = useState(loadFilters);
  const [operatori, setOperatori] = useState([]);
  const [operatoriAvailable, setOperatoriAvailable] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    sessionStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/commerciale/operatori`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (cancelled) return;
        setOperatoriAvailable(Boolean(json));
        setOperatori(Array.isArray(json?.data) ? json.data : []);
      })
      .catch(() => !cancelled && setOperatoriAvailable(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const updateFilters = (patch) => setFilters((current) => ({ ...current, ...patch }));
  const setPreset = (preset) =>
    updateFilters(preset === "custom" ? { preset } : { preset, ...periodFromPreset(preset) });
  const invalidPeriod = Boolean(filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo);
  const operatoreApplicabile = tab !== "economico" && tab !== "panoramica" && tab !== "marketing-social";

  const filterBar = (
    <>
      <div className="w-48">
        <Label htmlFor="dr-periodo" className="text-xs text-admin-muted">Periodo</Label>
        <select
          id="dr-periodo"
          className="mt-1 h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
          value={filters.preset}
          onChange={(e) => setPreset(e.target.value)}
        >
          {PERIOD_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
      </div>
      <div className="w-40">
        <Label htmlFor="dr-dal" className="text-xs text-admin-muted">Dal</Label>
        <Input id="dr-dal" type="date" className="mt-1 h-9" value={filters.dateFrom || ""}
          onChange={(e) => updateFilters({ preset: "custom", dateFrom: e.target.value })} />
      </div>
      <div className="w-40">
        <Label htmlFor="dr-al" className="text-xs text-admin-muted">Al</Label>
        <Input id="dr-al" type="date" className="mt-1 h-9" value={filters.dateTo || ""}
          onChange={(e) => updateFilters({ preset: "custom", dateTo: e.target.value })} />
      </div>
      <div>
        <span id="dr-servizio" className="text-xs text-admin-muted">Servizio</span>
        <div role="radiogroup" aria-labelledby="dr-servizio" className="mt-1 flex rounded-lg border border-admin-border bg-admin-bg p-0.5">
          {SERVIZI.map((s) => (
            <button
              key={s.id}
              type="button"
              role="radio"
              aria-checked={filters.servizio === s.id}
              onClick={() => updateFilters({ servizio: s.id })}
              className={`h-8 rounded-md px-3 text-sm font-medium transition ${
                filters.servizio === s.id ? "bg-admin-surface text-admin-accent shadow-sm" : "text-admin-muted hover:text-admin-text"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>
      <div className="min-w-[16rem] flex-1 basis-[16rem] sm:max-w-md">
        <Label htmlFor="dr-operatore" className="text-xs text-admin-muted">Operatore</Label>
        <select
          id="dr-operatore"
          className="mt-1 h-9 w-full max-w-full rounded-md border border-input bg-background px-2 text-sm disabled:opacity-60"
          value={filters.operatore}
          disabled={!operatoreApplicabile || !operatoriAvailable}
          onChange={(e) => updateFilters({ operatore: e.target.value })}
          title={
            !operatoreApplicabile
              ? "Filtro non applicabile a questa vista"
              : !operatoriAvailable
                ? "Elenco operatori non disponibile (serve il modulo Lead strutture)"
                : (operatori.find((o) => o.email === filters.operatore)?.name || filters.operatore || "Tutti gli operatori")
          }
        >
          <option value="">Tutti gli operatori</option>
          {operatori.map((o) => (
            <option key={o.email} value={o.email} title={o.email}>
              {o.name || o.email}
            </option>
          ))}
        </select>
      </div>
      <p className="basis-full text-xs text-admin-muted">
        {tab === "panoramica"
          ? "La panoramica mostra la situazione attuale: periodo e operatore non si applicano; il servizio filtra i riquadri."
          : tab === "economico" || tab === "marketing-social"
            ? "Operatore non attribuibile alle fonti economiche: il filtro non si applica."
            : !operatoriAvailable
              ? "Operatore disabilitato: l'elenco operatori richiede il modulo Lead strutture (gate attuale del backend)."
              : "Operatore disponibile su Operativo indipendentemente dal servizio. Si applica alle metriche attribuibili (lead strutture, demo, attività, inviti creati)."}
        {invalidPeriod ? <span className="ml-2 font-medium text-red-700">La data iniziale è successiva alla finale.</span> : null}
      </p>
    </>
  );

  if (!allowedTabs.length) {
    return <StateBlock state="error" title="Nessuna vista disponibile" description="Il tuo utente non ha i moduli Dashboard o Report." />;
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={LayoutDashboard}
        title="Dashboard e report"
        description="Panoramica, analisi operative per servizio, analisi economiche ed esportazioni in un'unica destinazione."
        actions={
          <Button type="button" variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>
            <RefreshCw className="mr-1.5 h-4 w-4" /> Aggiorna
          </Button>
        }
        filters={filterBar}
      />

      <Tabs value={tab} onValueChange={(value) => onTabChange && onTabChange(value)}>
        <TabsList className="h-auto flex-wrap justify-start gap-1 bg-admin-bg p-1">
          {allowedTabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id} className="data-[state=active]:text-admin-accent" data-testid={`dashboard-tab-${t.id}`}>
              {t.id === "panoramica" ? <LayoutDashboard className="mr-1.5 h-4 w-4" /> : t.id === "operativo" ? <ListChecks className="mr-1.5 h-4 w-4" /> : t.id === "marketing-social" ? <Share2 className="mr-1.5 h-4 w-4" /> : <Wallet className="mr-1.5 h-4 w-4" />}
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {access.panoramica ? (
          <TabsContent value="panoramica" className="mt-4">
            {tab === "panoramica" ? (
              <CommercialeDashboardModule key={`p-${refreshKey}`} embedded servizio={filters.servizio} />
            ) : null}
          </TabsContent>
        ) : null}

        <TabsContent value="operativo" className="mt-4 space-y-5">
          {tab === "operativo" && !invalidPeriod ? (
            <>
              <OperativoPanel key={`o-${refreshKey}`} filters={filters} onNavigate={onNavigate} />
              {access.report ? (
                filters.servizio === "cdrpt" ? (
                  <StateBlock
                    compact
                    state="empty"
                    title="Report commerciale non applicabile al servizio CDRPT"
                    description="Il report storico riguarda i lead strutture R&CO./Affidya. Seleziona Tutti o Affidya per vederlo ed esportarlo."
                  />
                ) : (
                  <ReportCommercialeModule
                    key={`r-${refreshKey}`}
                    embedded
                    filters={{ dateFrom: filters.dateFrom, dateTo: filters.dateTo, operatore: filters.operatore }}
                  />
                )
              ) : null}
            </>
          ) : null}
        </TabsContent>

        {access.economico ? (
          <TabsContent value="economico" className="mt-4">
            {tab === "economico" && !invalidPeriod ? (
              <EconomicoPanel
                key={`e-${refreshKey}`}
                filters={filters}
                onNavigate={adminUser?.scope === "super_admin" ? onNavigate : null}
              />
            ) : null}
          </TabsContent>
        ) : null}
        {access["marketing-social"] ? (
          <TabsContent value="marketing-social" className="mt-4">
            {tab === "marketing-social" && !invalidPeriod ? (
              <MarketingSocialReportPanel key={`m-${refreshKey}`} filters={filters} />
            ) : null}
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}

function MarketingSocialReportPanel({ filters }) {
  const params = queryString({
    from: filters.dateFrom,
    to: filters.dateTo,
    service: filters.servizio === "tutti" ? "" : filters.servizio,
  });
  const { loading, error, data, reload } = useJson(`${API}/marketing-social/report?${params}`);
  if (loading) return <StateBlock state="loading" title="Caricamento risultati Marketing e Social…" />;
  if (error) return <StateBlock state="error" description={error} onRetry={reload} />;
  const metrics = data?.metriche || data?.metrics || data?.funnel || [];
  const channels = data?.canali || data?.channels || data?.by_channel || [];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((metric) => (
          <KpiTile
            key={metric.id || metric.label}
            label={metric.label || metric.nome}
            value={formatCount(metric.valore ?? metric.value ?? metric.count ?? metric.attribuita ?? metric.attributed)}
            unavailable={metric.valore === null || metric.value === null}
            hint={metric.nota || metric.periodo || attributionSplit(metric)}
          />
        ))}
      </div>
      {!metrics.length ? <StateBlock compact state="empty" title="Nessun risultato per i filtri selezionati" /> : null}
      {channels.length ? (
        <Card className="border-admin-border shadow-sm">
          <CardHeader><CardTitle className="text-base">Risultati per canale</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {channels.map((channel) => (
              <div key={channel.id || channel.label} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-admin-border p-3 text-sm">
                <span className="font-medium text-admin-text">{channel.label || channel.nome}</span>
                <span className="text-admin-muted">{formatCount(channel.valore ?? channel.value ?? channel.count ?? channel.attribuita ?? channel.attributed)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function attributionSplit(metric) {
  const values = [
    ["Attribuita", metric.attribuita ?? metric.attributed],
    ["Non attribuibile", metric.non_attribuibile ?? metric.unattributable],
    ["Dato mancante", metric.dato_mancante ?? metric.missing],
  ].filter(([, value]) => value !== undefined && value !== null);
  return values.length ? values.map(([label, value]) => `${label}: ${value}`).join(" · ") : "";
}

function useJson(url) {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const response = await fetch(url, { headers: authHeaders() });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.detail || `Errore ${response.status}`);
      setState({ loading: false, error: "", data: json });
    } catch (error) {
      setState({ loading: false, error: error.message || "Errore di caricamento", data: null });
    }
  }, [url]);
  useEffect(() => {
    load();
  }, [load]);
  return { ...state, reload: load };
}

function OperativoPanel({ filters, onNavigate }) {
  const url = `${API}/dashboard-report/operativo?${queryString({
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    servizio: filters.servizio,
    operatore: filters.operatore,
  })}`;
  const { loading, error, data, reload } = useJson(url);

  if (loading) return <StateBlock state="loading" title="Caricamento riepilogo operativo…" />;
  if (error) return <StateBlock state="error" description={error} onRetry={reload} />;
  const sezioni = data?.sezioni || [];
  if (!sezioni.length) return <StateBlock state="empty" />;

  return (
    <div className="grid gap-4 2xl:grid-cols-2">
      {sezioni.map((sez) => (
        <Card key={sez.id} className="border-admin-border shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base text-admin-text">Riepilogo operativo · {sez.label}</CardTitle>
              <StatusBadge tone="accent">{sez.id === "cdrpt" ? "CasaDiRiposoPerTe" : "R&CO. / Affidya"}</StatusBadge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {!sez.disponibile ? (
              <StateBlock compact state="empty" title="Nessun dato visibile con i tuoi permessi" description={(sez.note || []).join(" ")} />
            ) : (
              <>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {sez.metriche.map((m) => (
                    <KpiTile
                      key={m.id}
                      label={m.label}
                      value={formatCount(m.valore)}
                      unavailable={m.valore === null || m.valore === undefined}
                      dependency={m.nota}
                      hint={m.nota}
                      status={m.stato === "parziale" ? <StatusBadge tone="warning">Dato parziale</StatusBadge> : null}
                      onClick={m.modulo && onNavigate ? () => onNavigate(m.modulo) : undefined}
                    />
                  ))}
                </div>
                <Distribution title={sez.id === "cdrpt" ? "Richieste del periodo per fase" : "Lead strutture del periodo per fase"} rows={sez.fasi} />
                {sez.demo_stati ? <Distribution title="Demo del periodo per stato" rows={sez.demo_stati} /> : null}
                {(sez.note || []).length ? (
                  <ul className="space-y-1 text-xs text-admin-muted">
                    {sez.note.map((n) => <li key={n}>• {n}</li>)}
                  </ul>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function Distribution({ title, rows = [] }) {
  const total = rows.reduce((sum, r) => sum + (Number(r.count) || 0), 0);
  return (
    <section aria-label={title}>
      <h3 className="mb-2 text-sm font-semibold text-admin-text">{title}</h3>
      {!rows.length ? (
        <p className="rounded-lg border border-dashed border-admin-border p-3 text-sm text-admin-muted">Nessun dato nel periodo selezionato.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => {
            const pct = total ? Math.round((r.count / total) * 100) : 0;
            return (
              <li key={r.label} className="grid grid-cols-[minmax(7rem,12rem)_1fr_auto] items-center gap-3 text-sm">
                <span className="truncate text-admin-text" title={r.label}>{r.label}</span>
                <span className="h-2 overflow-hidden rounded-full bg-admin-bg" aria-hidden>
                  <span className="block h-full rounded-full bg-admin-accent" style={{ width: `${pct}%` }} />
                </span>
                <span className="tabular-nums text-admin-muted">{r.count} <span className="text-xs">({pct}%)</span></span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function EconomicoPanel({ filters, onNavigate }) {
  const params = queryString({ date_from: filters.dateFrom, date_to: filters.dateTo, servizio: filters.servizio });
  const { loading, error, data, reload } = useJson(`${API}/dashboard-report/economico/summary?${params}`);
  const [exporting, setExporting] = useState(false);
  const [detail, setDetail] = useState(null);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/dashboard-report/economico/export?${params}`, { headers: authHeaders() });
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.detail || "Esportazione non riuscita");
      }
      const blob = await response.blob();
      const name = (response.headers.get("content-disposition") || "").match(/filename="?([^";]+)"?/i)?.[1] || "dashboard-economico.csv";
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(href);
      toast.success("Esportazione CSV scaricata");
    } catch (e) {
      toast.error(e.message || "Errore durante l'esportazione");
    } finally {
      setExporting(false);
    }
  };

  const openDetail = async (flusso, misura) => {
    setDetail({ flusso, misura, loading: true, righe: [] });
    try {
      const response = await fetch(
        `${API}/dashboard-report/economico/dettaglio?${queryString({ flusso, misura, date_from: filters.dateFrom, date_to: filters.dateTo, servizio: filters.servizio })}`,
        { headers: authHeaders() }
      );
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.detail || "Dettaglio non disponibile");
      setDetail({ flusso, misura, loading: false, ...json });
    } catch (e) {
      setDetail({ flusso, misura, loading: false, error: e.message, righe: [] });
    }
  };

  if (loading) return <StateBlock state="loading" title="Caricamento aggregati economici…" />;
  if (error) return <StateBlock state="error" description={error} onRetry={reload} />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-admin-border bg-admin-accent-soft/50 p-3 text-sm text-admin-text">
        <p className="max-w-3xl">
          Valori solo da fonti persistenti verificate. Imponibile, IVA e totale restano separati; nessuna aliquota viene
          presunta. Dove la fonte manca è indicato <strong>{NON_DISPONIBILE}</strong> con la dipendenza, mai zero.
          Lead e ingressi programmati non generano ricavi maturati.
        </p>
        <Button type="button" size="sm" onClick={exportCsv} disabled={exporting}>
          <Download className="mr-1.5 h-4 w-4" /> {exporting ? "Esportazione…" : "Esporta CSV"}
        </Button>
      </div>
      {data?.indicatori_partnership ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ["Opportunità aperte", data.indicatori_partnership.opportunita_aperte],
            ["Accordi sottoscritti", data.indicatori_partnership.accordi_sottoscritti],
            ["Partner attivate", data.indicatori_partnership.partner_attivate],
            ["Attività scadute", data.indicatori_partnership.attivita_scadute],
          ].map(([label, value]) => <Card key={label} className="border-admin-border"><CardContent className="p-4"><p className="text-xs text-admin-muted">{label}</p><p className="mt-1 text-2xl font-bold text-admin-text">{formatCount(value || 0)}</p></CardContent></Card>)}
        </div>
      ) : null}

      {(data?.flussi || []).map((flusso) => (
        <Card key={flusso.id} className="border-admin-border shadow-sm" data-testid={`economico-flusso-${flusso.id}`}>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base text-admin-text">{flusso.label}</CardTitle>
              <StatusBadge tone="accent">{flusso.servizio === "cdrpt" ? "CasaDiRiposoPerTe" : "R&CO. / Affidya"}</StatusBadge>
            </div>
            {flusso.condizioni ? (
              <p className="text-xs text-admin-muted">
                Condizioni per pratica: partner storiche €700 + IVA in due rate uguali; nuove partnership 50% della retta mensile, IVA inclusa, in due rate uguali. Gli aggregati usano solo le istantanee reali registrate all'ingresso.
              </p>
            ) : null}
            {flusso.listino ? (
              <p className="text-xs text-admin-muted">
                Prezzo al cliente: {formatEuro(flusso.listino.prezzo_mensile_iva_inclusa)}/mese IVA inclusa. Imponibile mensile:{" "}
                {flusso.listino.imponibile_mensile === null ? NON_DISPONIBILE : formatEuro(flusso.listino.imponibile_mensile)}. {flusso.listino.nota}
              </p>
            ) : null}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <caption className="sr-only">Misure economiche {flusso.label}</caption>
                <thead className="border-y border-admin-border bg-admin-bg/60">
                  <tr>
                    <th scope="col" className="px-4">Misura</th>
                    <th scope="col" className="text-right">Imponibile</th>
                    <th scope="col" className="text-right">IVA</th>
                    <th scope="col" className="text-right">Totale</th>
                    <th scope="col" className="text-right" title="Importo registrato sul documento: la base fiscale non è salvata">Importo documento</th>
                    <th scope="col" className="text-right">N.</th>
                    <th scope="col">Stato</th>
                    <th scope="col" className="px-4">Fonte / dipendenza</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-admin-border">
                  {MISURE.map(([id, label]) => {
                    const m = flusso.misure[id] || {};
                    const nd = m.stato === "non_disponibile";
                    const money = (v) => (nd || v === null || v === undefined ? <span className="text-admin-muted">—</span> : formatEuro(v));
                    const canDetail = !nd && flusso.id === "abbonamenti_affidya" && id !== "maturato";
                    const esclusi = Object.entries(m.esclusi || {}).filter(([, v]) => v);
                    return (
                      <tr key={id}>
                        <th scope="row" className="px-4 font-medium text-admin-text">{label}</th>
                        <td className="text-right tabular-nums">{money(m.imponibile)}</td>
                        <td className="text-right tabular-nums">{money(m.iva)}</td>
                        <td className="text-right tabular-nums">{money(m.totale)}</td>
                        <td className="text-right tabular-nums">{money(m.importo_documento)}</td>
                        <td className="text-right tabular-nums">{nd ? <span className="text-admin-muted">—</span> : formatCount(m.conteggio)}</td>
                        <td><StatoFonte stato={m.stato} /></td>
                        <td className="max-w-md px-4 text-xs text-admin-muted">
                          {nd ? <span><strong className="text-admin-text">{NON_DISPONIBILE}.</strong> {m.dipendenza}</span> : (
                            <span>
                              {m.fonte}
                              {m.nota ? <span className="block">{m.nota}</span> : null}
                              {esclusi.length ? (
                                <span className="block">Esclusi: {esclusi.map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(", ")}</span>
                              ) : null}
                            </span>
                          )}
                          {canDetail ? (
                            <button type="button" className="mt-1 block font-medium text-admin-accent underline-offset-2 hover:underline" onClick={() => openDetail(flusso.id, id)}>
                              Vedi documenti e pagamenti
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ))}

      <Dialog open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-h-[90vh] w-[96vw] max-w-5xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Dettaglio · {MISURE.find(([id]) => id === detail?.misura)?.[1]}</DialogTitle>
            <DialogDescription>Righe che compongono il dato, con gli stessi filtri e permessi del riepilogo.</DialogDescription>
          </DialogHeader>
          {detail?.loading ? <StateBlock compact state="loading" /> : detail?.error ? (
            <StateBlock compact state="error" description={detail.error} />
          ) : !detail?.righe?.length ? (
            <StateBlock compact state="empty" title="Nessuna riga nel periodo" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="border-b border-admin-border"><tr><th>Tipo</th><th>Data</th><th>Riferimento</th><th>Organizzazione</th><th className="text-right">Importo documento</th><th className="text-right">Importo pagamento</th></tr></thead>
                <tbody className="divide-y divide-admin-border">
                  {detail.righe.map((r) => (
                    <tr key={`${r.tipo}-${r.id}`}>
                      <td><StatusBadge tone={r.tipo === "fattura" ? "info" : "success"}>{r.tipo === "fattura" ? "Fattura" : "Pagamento"}</StatusBadge></td>
                      <td className="tabular-nums">{r.data || "—"}</td>
                      <td>{r.riferimento}</td>
                      <td>{r.organizzazione || r.organizzazione_id || "—"}</td>
                      <td className="text-right tabular-nums">{r.importo_documento === null ? "—" : formatEuro(r.importo_documento)}</td>
                      <td className="text-right tabular-nums">{r.importo_pagamento === null ? (r.tipo === "pagamento" ? "Importo non registrato" : "—") : formatEuro(r.importo_pagamento)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {onNavigate ? (
            <div className="flex justify-end">
              <Button type="button" variant="outline" size="sm" onClick={() => { setDetail(null); onNavigate("affidya-fic"); }}>
                <ExternalLink className="mr-1.5 h-4 w-4" /> Apri Fatturazione abbonamenti
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <p className="flex items-center gap-1.5 text-xs text-admin-muted">
        <BarChart3 className="h-3.5 w-3.5" aria-hidden /> {data?.nota_operatore}
      </p>
    </div>
  );
}
